import React from 'react';
import styled, { css } from 'styled-components';
import { KeyboardDetectionProvider, ButtonTertiary, cssColorValue, makeColorSetClass, screenSmMin, spacer8, spacer16, spacer24, spacer32, spacer40, Type, black, white, } from '@spotify-internal/encore-web';
import { DELAYS, fadeInAnimation, ImageAspectBox21x9, withAnimationContext, Grid, CarouselNav, ResponsiveType, ContentfulImage, TWO_COLUMN_MAX_WIDTH, } from '../../ui';
import { withoutProps } from '../../ui/utils';
import { Quote } from '../SingleTestimonialModule/Quote';
import { ColorSetWrapper } from '../../ui/Colors/Colors';
const Crossfade = css `
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  opacity: ${({ active }) => (active ? 1 : 0)};
  z-index: ${({ active }) => (active ? 1 : 'unset')};
  transition: visibility 0.5s, opacity 0.5s ease-in-out, z-index 0s 0.5s,
    color 0s 0.5s;
`;
const GroupTestimonial = styled(Grid) `
  padding-top: ${spacer24};
  padding-bottom: ${spacer40};

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    padding-bottom: ${spacer32};
  }
`;
const ImageContainer = styled(ImageAspectBox21x9) `
  grid-column: 1 / -1;

  margin-bottom: ${spacer32};
`;
const CrossfadeImage = styled.div `
  overflow: hidden;
  position: absolute;
  height: 100%;
  width: 100%;

  ${Crossfade}
`;
const Supertext = withAnimationContext(styled(ResponsiveType).attrs({
    typeAs: 'h3',
    variantDesktop: 'ballad',
    variantMobile: 'viola',
}) `
    text-transform: uppercase;
    margin-bottom: ${spacer8};
  `);
const Headline = withAnimationContext(styled(ResponsiveType).attrs({
    typeAs: 'h2',
    variantDesktop: 'brio',
    variantMobile: 'alto',
}) `
    ${fadeInAnimation(DELAYS.SECOND)};
    margin-bottom: ${spacer24};
  `);
const HeadlineSupertextBody = styled.div `
  grid-column: 1 / 5;

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
  }
`;
const NameList = styled.div `
  // force two columns when more than 7 names
  ${({ nameCount }) => nameCount >= 7 ? 'flex-direction: row;' : 'flex-direction: column;'}
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    display: none;
  }
`;
const NameWrapper = styled.div `
  width: 50%;
  display: flex;
`;
const Name = withAnimationContext(styled(withoutProps(ButtonTertiary, 'animationContext')) `
    ${fadeInAnimation(DELAYS.THIRD)};
    transition: 0.2s ease-in-out;
    padding-bottom: ${spacer16};
    display: flex;
    text-align: left;
    &::after,
    &:focus::after {
      bottom: 6px;
    }
    &:focus,
    &:hover:not(:focus):not(:disabled) {
      color: ${cssColorValue('textSubdued')};
      transform: translateX(-${spacer8});
    }
  `);
const QuoteContainer = styled.div `
  grid-column: 6 / -1;

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
  }

  display: grid;
`;
const CrossfadeText = styled.div `
  grid-column: 1;
  grid-row: 1;
  display: grid;

  ${Crossfade}
`;
const CarouselNavContainer = styled.div `
  @media (min-width: ${screenSmMin}) {
    display: none;
  }

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    padding-top: ${spacer24};
    grid-column: 1 / -1;
  }
`;
export const GroupTestimonialModule = React.forwardRef(({ testimonials = [], themeName = '', colors }, ref) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const handlePrev = () => {
        setActiveIndex(Math.max(activeIndex - 1, 0));
    };
    const handleNext = () => {
        setActiveIndex(Math.min(activeIndex + 1, testimonials.length - 1));
    };
    const jumpToTestimonial = (index) => {
        setActiveIndex(index);
    };
    const items = testimonials
        .filter(Boolean)
        .filter((testimonial) => !testimonial.sys);
    const showImage = items.every(testimonial => testimonial.image);
    const activeTestimonial = items[activeIndex];
    const textBaseColor = themeName === 'blackOnWhite' ? black : white;
    const useColors = (colors === null || colors === void 0 ? void 0 : colors.backgroundColor) && (colors === null || colors === void 0 ? void 0 : colors.textColor);
    return (React.createElement(KeyboardDetectionProvider, null,
        React.createElement(ColorSetWrapper, { colors: colors },
            React.createElement(GroupTestimonial, { className: !useColors
                    ? makeColorSetClass(themeName === 'blackOnWhite' ? 'base' : 'invertedDark')
                    : '' },
                showImage && (React.createElement(ImageContainer, null, items.map((testimonial, index) => testimonial.image && (React.createElement(CrossfadeImage, { key: `testimonial-image-${index}`, active: activeIndex === index },
                    React.createElement(ContentfulImage, { asset: testimonial.image, caption: testimonial.caption })))))),
                React.createElement(HeadlineSupertextBody, null,
                    React.createElement(Supertext, null, activeTestimonial === null || activeTestimonial === void 0 ? void 0 : activeTestimonial.supertext),
                    React.createElement(Headline, null, activeTestimonial === null || activeTestimonial === void 0 ? void 0 : activeTestimonial.source),
                    React.createElement(NameList, { nameCount: items.length }, items.map((testimonial, index) => (React.createElement(React.Fragment, null, activeIndex !== index && (React.createElement(NameWrapper, null,
                        React.createElement(Name, { role: "button", onClick: () => jumpToTestimonial(index), key: `testimonial-${testimonial.source}`, name: testimonial.source, condensedAll: true },
                            React.createElement(Type, { variant: "violaBold" }, testimonial.source))))))))),
                React.createElement(QuoteContainer, { ref: ref }, items.map((testimonial, index) => (React.createElement(CrossfadeText, { key: `testimonial-text-${index}`, active: activeIndex === index },
                    React.createElement(Quote, { quote: testimonial.quote, source: testimonial.source, jobTitle: testimonial.jobTitle }))))),
                React.createElement(CarouselNavContainer, null,
                    React.createElement(CarouselNav, { index: activeIndex + 1, total: items.length, handlePrev: handlePrev, handleNext: handleNext, hexColor: textBaseColor }))))));
});
//# sourceMappingURL=index.js.map